import {
  type FormEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";

import {
  APIError,
  api,
  type Invitation,
  type InvitationRole,
  type Membership,
  type Organization,
  type PasskeyRecord,
  type Project,
  type Role,
} from "./api/client";
import { SignIn, useAuth } from "./auth/AuthProvider";
import {
  createPasskey,
  type JSONCreationOptions,
  passkeyErrorMessage,
} from "./auth/webauthn";
import { Audit } from "./routes/Audit";
import { DocumentDetail } from "./routes/DocumentDetail";
import { Documents } from "./routes/Documents";
import {
  Explore,
  ListingUnavailable,
  PublicListingPage,
  publicListingSlug,
} from "./routes/Explore";

export function App(): ReactElement {
  const { actor, recoveryCodes, status, signOut, clearRecoveryCodes } =
    useAuth();
  const [locationKey, setLocationKey] = useState(window.location.href);
  const pathname = window.location.pathname;

  useEffect(() => {
    const update = (): void => setLocationKey(window.location.href);
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  useEffect(() => {
    const authenticationPath = pathname === "/signin" || pathname === "/signup";
    if (
      authenticationPath &&
      status === "authenticated" &&
      actor &&
      !recoveryCodes
    ) {
      window.history.replaceState(null, "", "/workspace");
      setLocationKey(window.location.href);
    }
  }, [actor, pathname, recoveryCodes, status]);

  const invitationToken = invitationTokenFromPath(pathname);
  if (pathname === "/join" || pathname.startsWith("/join/")) {
    return invitationToken ? (
      <JoinInvitation token={invitationToken} />
    ) : (
      <InvitationUnavailable />
    );
  }

  if (pathname === "/") {
    return (
      <MarketingPage authenticated={status === "authenticated" && !!actor} />
    );
  }

  if (pathname === "/explore") {
    return <Explore />;
  }

  if (pathname === "/m" || pathname.startsWith("/m/")) {
    const slug = publicListingSlug(pathname);
    return slug ? <PublicListingPage slug={slug} /> : <ListingUnavailable />;
  }

  if (pathname === "/signin" || pathname === "/signup") {
    if (status === "authenticated" && actor) {
      return (
        <>
          {recoveryCodes && (
            <RecoveryCodesNotice
              codes={recoveryCodes}
              onSaved={clearRecoveryCodes}
            />
          )}
          <WorkspaceShell locationKey={locationKey} signOut={signOut} />
        </>
      );
    }
    return (
      <SignIn initialView={pathname === "/signup" ? "signup" : "signin"} />
    );
  }

  if (!pathname.startsWith("/workspace")) return <PublicNotFound />;

  if (status === "checking") return <BootScreen />;
  if (status === "unauthenticated" || !actor) {
    return <SignIn initialView="signin" />;
  }

  return (
    <>
      {recoveryCodes && (
        <RecoveryCodesNotice
          codes={recoveryCodes}
          onSaved={clearRecoveryCodes}
        />
      )}
      <WorkspaceShell locationKey={locationKey} signOut={signOut} />
    </>
  );
}

function BootScreen(): ReactElement {
  return (
    <div
      className="boot-screen"
      aria-label="Checking your session"
      aria-busy="true"
    >
      <VibeCADLogo title="VibeCAD" size={52} />
      <div className="boot-line">
        <span />
      </div>
    </div>
  );
}

function WorkspaceShell({
  locationKey,
  signOut,
}: {
  locationKey: string;
  signOut: () => Promise<void>;
}): ReactElement {
  const { actor } = useAuth();
  if (!actor) return <BootScreen />;

  const pathname = window.location.pathname;
  const securityActive = pathname === "/workspace/security";
  const teamsActive = pathname.startsWith("/workspace/teams");
  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="wordmark" href="/" aria-label="VibeCAD home">
          <VibeCADLogo title="" size={34} />
          <span>VibeCAD</span>
        </a>
        <nav aria-label="Workspace navigation">
          <a href="/explore">Explore</a>
          <a
            className={!securityActive && !teamsActive ? "active" : ""}
            href="/workspace"
          >
            Documents
          </a>
          <a className={teamsActive ? "active" : ""} href="/workspace/teams">
            Teams
          </a>
          <a
            className={securityActive ? "active" : ""}
            href="/workspace/security"
          >
            Security
          </a>
        </nav>
        <div className="account-menu">
          <span className="avatar">{initials(actor.display_name)}</span>
          <span className="account-copy">
            <strong>{actor.display_name}</strong>
            <small>{actor.email || "Authenticated member"}</small>
          </span>
          <button
            className="icon-button"
            onClick={() => void signOut()}
            type="button"
          >
            Sign out
          </button>
        </div>
      </header>
      <RouteContent key={locationKey} />
      <footer className="site-footer">
        <span>VibeCAD Workspace</span>
        <span>Opaque storage · Immutable history · Tenant scoped</span>
      </footer>
    </div>
  );
}

function RouteContent(): ReactElement {
  const route = parseWorkspaceRoute(window.location.pathname);
  switch (route.name) {
    case "documents":
      return (
        <Documents
          organizationId={route.organizationId}
          projectId={route.projectId}
        />
      );
    case "document":
      return <DocumentDetail documentId={route.documentId} />;
    case "audit":
      return <Audit organizationId={route.organizationId} />;
    case "teams":
      return <Teams />;
    case "team":
      return <TeamDetail organizationId={route.organizationId} />;
    case "security":
      return <AccountSecurity />;
    default:
      return <NotFound />;
  }
}

type WorkspaceRoute =
  | { name: "documents"; organizationId?: string; projectId?: string }
  | { name: "document"; documentId: string }
  | { name: "audit"; organizationId: string }
  | { name: "teams" }
  | { name: "team"; organizationId: string }
  | { name: "security" }
  | { name: "not-found" };

function parseWorkspaceRoute(pathname: string): WorkspaceRoute {
  const path = pathname.replace(/^\/workspace(?=\/|$)/, "") || "/";
  if (path === "/") return { name: "documents" };
  if (path === "/teams") return { name: "teams" };
  let match = path.match(/^\/teams\/([^/]+)$/);
  if (match) {
    const organizationId = decodeRouteIdentifier(match[1]);
    return organizationId
      ? { name: "team", organizationId }
      : { name: "not-found" };
  }
  if (path === "/security") return { name: "security" };
  match = path.match(/^\/organizations\/([^/]+)$/);
  if (match) {
    const organizationId = decodeRouteIdentifier(match[1]);
    return organizationId
      ? { name: "documents", organizationId }
      : { name: "not-found" };
  }
  match = path.match(/^\/organizations\/([^/]+)\/projects\/([^/]+)$/);
  if (match) {
    const organizationId = decodeRouteIdentifier(match[1]);
    const projectId = decodeRouteIdentifier(match[2]);
    if (!organizationId || !projectId) return { name: "not-found" };
    return { name: "documents", organizationId, projectId };
  }
  match = path.match(/^\/organizations\/([^/]+)\/audit$/);
  if (match) {
    const organizationId = decodeRouteIdentifier(match[1]);
    return organizationId
      ? { name: "audit", organizationId }
      : { name: "not-found" };
  }
  match = path.match(/^\/documents\/([^/]+)$/);
  if (match) {
    const documentId = decodeRouteIdentifier(match[1]);
    return documentId
      ? { name: "document", documentId }
      : { name: "not-found" };
  }
  return { name: "not-found" };
}

function decodeRouteIdentifier(value: string): string | null {
  try {
    const decoded = decodeURIComponent(value);
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      decoded,
    )
      ? decoded
      : null;
  } catch {
    return null;
  }
}

function invitationTokenFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/join\/([^/]+)$/);
  if (!match) return null;
  try {
    const token = decodeURIComponent(match[1]);
    return /^[A-Za-z0-9_-]{43}$/.test(token) ? token : null;
  } catch {
    return null;
  }
}

function InvitationUnavailable(): ReactElement {
  return (
    <main className="public-not-found invitation-unavailable">
      <VibeCADLogo title="VibeCAD" size={52} />
      <p className="eyebrow">Team invitation</p>
      <h1>This invitation is unavailable.</h1>
      <p>
        The link may be invalid, expired, revoked, or already used. Ask a team
        owner or admin for a new invitation.
      </p>
      <a className="button button-primary" href="/">
        Return to VibeCAD
      </a>
    </main>
  );
}

function JoinInvitation({ token }: { token: string }): ReactElement {
  const {
    actor,
    clearRecoveryCodes,
    memberships,
    recoveryCodes,
    refreshIdentity,
    status,
  } = useAuth();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .inspectInvitation(token)
      .then((result) => {
        if (!active) return;
        setInvitation(result);
        setUnavailable(false);
      })
      .catch(() => {
        if (active) setUnavailable(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const joined =
    accepted ||
    (!!invitation &&
      memberships.some(
        (membership) =>
          membership.organization_id === invitation.organization_id &&
          membership.status === "active",
      ));

  async function accept(): Promise<void> {
    setAccepting(true);
    setError(null);
    try {
      await api.acceptInvitation(token);
      await refreshIdentity();
      setAccepted(true);
    } catch (cause) {
      setError(invitationActionMessage(cause));
    } finally {
      setAccepting(false);
    }
  }

  if (loading || status === "checking") return <BootScreen />;
  if (unavailable || !invitation) return <InvitationUnavailable />;
  if (status === "unauthenticated" || !actor) {
    return (
      <SignIn
        initialView="signin"
        invitationToken={token}
        invitationOrganizationName={invitation.organization_name ?? "this team"}
      />
    );
  }

  return (
    <main className="join-shell">
      {recoveryCodes && (
        <RecoveryCodesNotice
          codes={recoveryCodes}
          onSaved={clearRecoveryCodes}
        />
      )}
      <section className="join-card" aria-labelledby="join-title">
        <a className="auth-wordmark" href="/" aria-label="VibeCAD home">
          <VibeCADLogo title="" size={44} />
          <span>VibeCAD</span>
        </a>
        <p className="eyebrow">Team invitation</p>
        <h1 id="join-title">
          {joined
            ? `You joined ${invitation.organization_name ?? "the team"}.`
            : `Join ${invitation.organization_name ?? "this team"}`}
        </h1>
        <p>
          {joined
            ? "Your membership is active and this one-time invitation can no longer be reused."
            : `${actor.display_name}, confirm that you want to join with the ${invitation.role} role.`}
        </p>
        <dl className="invitation-summary">
          <div>
            <dt>Team</dt>
            <dd>{invitation.organization_name ?? "VibeCAD team"}</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd className={`role-badge role-${invitation.role}`}>
              {invitation.role}
            </dd>
          </div>
          <div>
            <dt>Expires</dt>
            <dd>{formatTeamDate(invitation.expires_at)}</dd>
          </div>
        </dl>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {joined ? (
          <a
            className="button button-primary button-wide"
            href={`/workspace/teams/${encodeURIComponent(invitation.organization_id)}`}
          >
            Open team
          </a>
        ) : (
          <button
            className="button button-primary button-wide"
            disabled={accepting}
            onClick={() => void accept()}
            type="button"
          >
            {accepting ? "Joining team…" : "Join team"}
          </button>
        )}
        <p className="fine-print">
          Invitation links are single-use capabilities. VibeCAD never asks you
          to paste the link or a token into another page.
        </p>
      </section>
    </main>
  );
}

function Teams(): ReactElement {
  const [teams, setTeams] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .organizations()
      .then((page) => {
        if (active) setTeams(page.items);
      })
      .catch((cause) => {
        if (active) setError(teamActionMessage(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  async function createTeam(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    setError(null);
    setMessage(null);
    try {
      const team = await api.createOrganization(name.trim());
      setTeams((current) => [...current, team]);
      setName("");
      setMessage(`${team.name} was created. You are its owner.`);
    } catch (cause) {
      setError(teamActionMessage(cause));
    } finally {
      setCreating(false);
    }
  }

  function switchTeam(organizationId: string): void {
    if (!organizationId) return;
    navigateWithinApp(`/workspace/teams/${encodeURIComponent(organizationId)}`);
  }

  return (
    <main className="page page-teams">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Collaboration</p>
          <h1>Teams</h1>
          <p>Create a team, switch context, and manage secure invitations.</p>
        </div>
      </header>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {message && (
        <div className="alert alert-success" role="status">
          {message}
        </div>
      )}
      <section className="team-layout">
        <div className="settings-panel">
          <p className="eyebrow">Your teams</p>
          <h2>Switch team</h2>
          {loading ? (
            <p aria-busy="true">Loading teams…</p>
          ) : teams.length === 0 ? (
            <p>You do not have a team yet.</p>
          ) : (
            <>
              <label htmlFor="team-switcher">Team</label>
              <select
                id="team-switcher"
                defaultValue=""
                onChange={(event) => switchTeam(event.target.value)}
              >
                <option value="" disabled>
                  Choose a team
                </option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name} · {team.actor_role}
                  </option>
                ))}
              </select>
              <div className="team-card-grid" aria-label="Teams">
                {teams.map((team) => (
                  <a
                    className="team-card"
                    href={`/workspace/teams/${encodeURIComponent(team.id)}`}
                    key={team.id}
                  >
                    <span className={`role-badge role-${team.actor_role}`}>
                      {team.actor_role}
                    </span>
                    <strong>{team.name}</strong>
                    <small>Open members, projects, and invitations →</small>
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
        <form
          className="settings-panel"
          onSubmit={(event) => void createTeam(event)}
        >
          <p className="eyebrow">New team</p>
          <h2>Create a team</h2>
          <p>
            You become the owner and can invite admins, members, or viewers.
          </p>
          <label htmlFor="team-name">Team name</label>
          <input
            id="team-name"
            maxLength={120}
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <button
            className="button button-primary"
            disabled={creating || !name.trim()}
            type="submit"
          >
            {creating ? "Creating team…" : "Create team"}
          </button>
        </form>
      </section>
    </main>
  );
}

interface TeamResources {
  team: Organization;
  memberships: Membership[];
  invitations: Invitation[];
  projects: Project[];
}

function TeamDetail({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  const [resources, setResources] = useState<TeamResources | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);
  const [invitationRole, setInvitationRole] =
    useState<InvitationRole>("member");
  const [creatingInvitation, setCreatingInvitation] = useState(false);
  const [oneTimeJoinURL, setOneTimeJoinURL] = useState<string | null>(null);
  const [oneTimeInvitationID, setOneTimeInvitationID] = useState<string | null>(
    null,
  );
  const [copyState, setCopyState] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api
      .organization(organizationId)
      .then(async (team) => {
        const [memberships, projects, invitations] = await Promise.all([
          api.memberships(organizationId),
          api.projects(organizationId),
          team.actor_role === "owner" || team.actor_role === "admin"
            ? api.invitations(organizationId)
            : Promise.resolve({ items: [] }),
        ]);
        if (active) {
          setResources({
            team,
            memberships: memberships.items,
            invitations: invitations.items,
            projects: projects.items,
          });
        }
      })
      .catch((cause) => {
        if (active) setError(teamActionMessage(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [organizationId]);

  const role = resources?.team.actor_role ?? "viewer";
  const canManageInvitations = role === "owner" || role === "admin";
  const canCreateProject = role !== "viewer";
  const invitationRoles = useMemo<InvitationRole[]>(
    () =>
      role === "owner" ? ["viewer", "member", "admin"] : ["viewer", "member"],
    [role],
  );

  useEffect(() => {
    if (!invitationRoles.includes(invitationRole)) {
      setInvitationRole("member");
    }
  }, [invitationRole, invitationRoles]);

  async function createProject(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    if (!projectName.trim() || !resources) return;
    setCreatingProject(true);
    setError(null);
    setMessage(null);
    try {
      const project = await api.createProject(
        organizationId,
        projectName.trim(),
        projectDescription.trim(),
      );
      setResources({
        ...resources,
        projects: [...resources.projects, project],
      });
      setProjectName("");
      setProjectDescription("");
      setMessage(`${project.name} was created.`);
    } catch (cause) {
      setError(teamActionMessage(cause));
    } finally {
      setCreatingProject(false);
    }
  }

  async function createInvitation(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    if (!resources) return;
    setCreatingInvitation(true);
    setError(null);
    setMessage(null);
    setCopyState(null);
    try {
      const created = await api.createInvitation(
        organizationId,
        invitationRole,
      );
      setResources({
        ...resources,
        invitations: [created.invitation, ...resources.invitations],
      });
      setOneTimeJoinURL(new URL(created.join_url, window.location.origin).href);
      setOneTimeInvitationID(created.invitation.id);
      setMessage(
        "Invitation created. Copy its link now; it will not be shown again.",
      );
    } catch (cause) {
      setError(teamActionMessage(cause));
    } finally {
      setCreatingInvitation(false);
    }
  }

  async function copyInvitation(): Promise<void> {
    if (!oneTimeJoinURL) return;
    try {
      await navigator.clipboard.writeText(oneTimeJoinURL);
      setCopyState("Invitation link copied.");
    } catch {
      setCopyState("Copy failed. Select the link and copy it manually.");
    }
  }

  async function revokeInvitation(invitation: Invitation): Promise<void> {
    if (!resources) return;
    setError(null);
    setMessage(null);
    try {
      await api.revokeInvitation(organizationId, invitation.id);
      const updated = await api.invitations(organizationId);
      setResources({
        ...resources,
        invitations: updated.items,
      });
      if (oneTimeInvitationID === invitation.id) {
        setOneTimeJoinURL(null);
        setOneTimeInvitationID(null);
        setCopyState(null);
      }
      setMessage("Invitation revoked.");
    } catch (cause) {
      setError(teamActionMessage(cause));
    }
  }

  if (loading) {
    return (
      <main className="page page-teams" aria-busy="true">
        <p>Loading team…</p>
      </main>
    );
  }
  if (!resources) {
    return (
      <main className="page page-teams">
        <section className="empty-state error-state" role="alert">
          <h1>We couldn’t open this team</h1>
          <p>
            {error ?? "The team is unavailable or you no longer have access."}
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="page page-teams">
      <header className="page-heading team-heading">
        <div>
          <p className="eyebrow">Team workspace</p>
          <h1>{resources.team.name}</h1>
          <p>
            <span className={`role-badge role-${role}`}>{role}</span> Manage
            projects, members, and secure invitation links.
          </p>
        </div>
        <div className="page-actions">
          <a
            className="button button-primary"
            href={`/workspace/organizations/${encodeURIComponent(organizationId)}`}
          >
            Open documents
          </a>
          <a className="button button-quiet" href="/workspace/teams">
            All teams
          </a>
        </div>
      </header>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {message && (
        <div className="alert alert-success" role="status">
          {message}
        </div>
      )}

      <section className="team-detail-grid">
        <div className="settings-panel team-projects">
          <p className="eyebrow">Team library</p>
          <h2>Projects</h2>
          {resources.projects.length === 0 ? (
            <p>No projects yet.</p>
          ) : (
            <ul className="resource-list">
              {resources.projects.map((project) => (
                <li key={project.id}>
                  <div>
                    <strong>{project.name}</strong>
                    <small>
                      {project.description || "Shared VibeCAD project"}
                    </small>
                  </div>
                  <a
                    href={`/workspace/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(project.id)}`}
                  >
                    Open documents →
                  </a>
                </li>
              ))}
            </ul>
          )}
          {canCreateProject ? (
            <form
              className="inline-form"
              onSubmit={(event) => void createProject(event)}
            >
              <label htmlFor="project-name">Project name</label>
              <input
                id="project-name"
                maxLength={120}
                required
                value={projectName}
                onChange={(event) => setProjectName(event.target.value)}
              />
              <label htmlFor="project-description">
                Description (optional)
              </label>
              <input
                id="project-description"
                maxLength={500}
                value={projectDescription}
                onChange={(event) => setProjectDescription(event.target.value)}
              />
              <button
                className="button button-primary"
                disabled={creatingProject || !projectName.trim()}
                type="submit"
              >
                {creatingProject ? "Creating project…" : "Create project"}
              </button>
            </form>
          ) : (
            <p className="permission-note">
              Viewers can browse projects but cannot create them.
            </p>
          )}
        </div>

        <div className="settings-panel team-members">
          <p className="eyebrow">Access</p>
          <h2>Members</h2>
          <ul className="resource-list member-list" aria-label="Team members">
            {resources.memberships.map((membership) => (
              <li key={membership.id}>
                <span className="avatar">
                  {initials(membership.actor.display_name)}
                </span>
                <div>
                  <strong>{membership.actor.display_name}</strong>
                  <small>@{membership.actor.handle}</small>
                </div>
                <span className={`role-badge role-${membership.role}`}>
                  {membership.role}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="settings-panel invitation-panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Capability links</p>
            <h2>Invitations</h2>
            <p>
              Links expire after 72 hours, can be used once, and never appear in
              this list after creation.
            </p>
          </div>
        </div>
        {canManageInvitations ? (
          <>
            <form
              className="invite-form"
              onSubmit={(event) => void createInvitation(event)}
            >
              <label htmlFor="invitation-role">Role</label>
              <select
                id="invitation-role"
                value={invitationRole}
                onChange={(event) =>
                  setInvitationRole(event.target.value as InvitationRole)
                }
              >
                {invitationRoles.map((candidate) => (
                  <option key={candidate} value={candidate}>
                    {candidate}
                  </option>
                ))}
              </select>
              <button
                className="button button-primary"
                disabled={creatingInvitation}
                type="submit"
              >
                {creatingInvitation
                  ? "Creating invitation…"
                  : "Create invitation"}
              </button>
            </form>
            {oneTimeJoinURL && (
              <div
                className="one-time-link"
                role="group"
                aria-label="One-time invitation link"
              >
                <strong>Copy this link now</strong>
                <p>It is displayed once and cannot be recovered later.</p>
                <div>
                  <input
                    aria-label="Invitation link"
                    readOnly
                    value={oneTimeJoinURL}
                  />
                  <button
                    className="button button-quiet"
                    onClick={() => void copyInvitation()}
                    type="button"
                  >
                    Copy link
                  </button>
                </div>
                {copyState && <small role="status">{copyState}</small>}
              </div>
            )}
            {resources.invitations.length === 0 ? (
              <p>No invitations have been created.</p>
            ) : (
              <ul
                className="resource-list invitation-list"
                aria-label="Invitations"
              >
                {resources.invitations.map((invitation) => {
                  const canRevoke =
                    invitation.status === "pending" &&
                    (role === "owner" || invitation.role !== "admin");
                  return (
                    <li key={invitation.id}>
                      <div>
                        <strong
                          className={`role-badge role-${invitation.role}`}
                        >
                          {invitation.role}
                        </strong>
                        <small>
                          Expires {formatTeamDate(invitation.expires_at)}
                        </small>
                      </div>
                      <span
                        className={`invitation-status status-${invitation.status}`}
                      >
                        {invitation.status}
                      </span>
                      {canRevoke && (
                        <button
                          className="button button-danger"
                          onClick={() => void revokeInvitation(invitation)}
                          type="button"
                        >
                          Revoke
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        ) : (
          <p className="permission-note">
            Only team owners and admins can create or revoke invitations.
          </p>
        )}
      </section>
    </main>
  );
}

function invitationActionMessage(cause: unknown): string {
  if (cause instanceof APIError && cause.code === "invitation_unavailable") {
    return "This invitation is invalid, expired, revoked, already used, or no longer available.";
  }
  if (cause instanceof APIError && cause.code === "forbidden") {
    return "Your account is not allowed to accept this invitation.";
  }
  return teamActionMessage(cause);
}

function teamActionMessage(cause: unknown): string {
  if (cause instanceof APIError) {
    if (cause.code === "forbidden") {
      return "Your team role does not allow that action.";
    }
    if (cause.code === "not_found") {
      return "That team resource is unavailable or you no longer have access.";
    }
    return `${cause.message} · Request ${cause.requestId}`;
  }
  return cause instanceof Error
    ? cause.message
    : "The request could not be completed.";
}

function formatTeamDate(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function navigateWithinApp(path: string): void {
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function VibeCADLogo({
  title,
  size = 32,
}: {
  title: string;
  size?: number;
}): ReactElement {
  const gradientId = `vibecad-mark-${useId().replace(/:/g, "")}`;
  return (
    <svg
      className="vibecad-logo"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <defs>
        <linearGradient
          id={gradientId}
          x1="10"
          y1="54"
          x2="54"
          y2="10"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#1c7ed6" />
          <stop offset="1" stopColor="#74c0fc" />
        </linearGradient>
      </defs>
      <rect x="6" y="6" width="52" height="52" rx="13" fill="#0e1116" />
      <path
        d="M18 18h12l-4.8 16.6c-.4 1.5.7 2.9 2.2 2.9h2.5l-3.1 8.5h-8.7l2.2-6.3c-5.6-2.2-9.1-7.4-9.1-13.8 0-3.1.8-5.8 2.4-7.9z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M34 18h12.2c4.6 0 7.8 3.1 7.8 7.6 0 3.3-1.8 5.8-4.8 7 2.2.9 3.6 3.1 3.6 6 0 5-3.5 8.4-8.7 8.4H31l2.7-8.5h8.2c1.6 0 2.7-1 2.7-2.4s-1-2.3-2.7-2.3h-5.8l2.3-7.3h5.4c1.5 0 2.5-.9 2.5-2.3 0-1.3-1-2.1-2.5-2.1H32.7L34 18z"
        fill="#e8edf2"
      />
      <path
        d="M20.2 50h23.6"
        fill="none"
        stroke="#4dabf7"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const GITHUB_REPOSITORY = "https://github.com/10-X-eng/vibecad";
const RELEASES_URL = `${GITHUB_REPOSITORY}/releases`;

export const RELEASE_FALLBACKS = {
  appImage: `${RELEASES_URL}/latest`,
  deb: `${RELEASES_URL}/latest`,
  windowsInstaller: `${RELEASES_URL}/latest`,
  windowsPortable: `${RELEASES_URL}/latest`,
  macArm: `${RELEASES_URL}/latest`,
  macIntel: `${RELEASES_URL}/latest`,
} as const;

type ReleaseLinks = Record<keyof typeof RELEASE_FALLBACKS, string>;
interface GitHubAsset {
  name?: unknown;
  browser_download_url?: unknown;
}
interface GitHubRelease {
  assets?: unknown;
}

export function resolveReleaseAssets(payload: unknown): ReleaseLinks {
  if (!Array.isArray(payload)) return { ...RELEASE_FALLBACKS };
  const releases = payload.filter(
    (release): release is GitHubRelease =>
      typeof release === "object" && release !== null,
  );
  const assets = releases.flatMap((release) =>
    Array.isArray(release.assets)
      ? release.assets.filter(
          (asset): asset is GitHubAsset =>
            typeof asset === "object" && asset !== null,
        )
      : [],
  );
  const find = (pattern: RegExp, fallback: string): string => {
    const asset = assets.find(
      (candidate) =>
        typeof candidate.name === "string" && pattern.test(candidate.name),
    );
    return asset &&
      typeof asset.browser_download_url === "string" &&
      /^https:\/\/github\.com\/10-X-eng\/vibecad\/releases\/download\//.test(
        asset.browser_download_url,
      )
      ? asset.browser_download_url
      : fallback;
  };
  return {
    appImage: find(/AppImage$/i, RELEASE_FALLBACKS.appImage),
    deb: find(/\.deb$/i, RELEASE_FALLBACKS.deb),
    windowsInstaller: find(
      /installer\.exe$/i,
      RELEASE_FALLBACKS.windowsInstaller,
    ),
    windowsPortable: find(/\.7z$/i, RELEASE_FALLBACKS.windowsPortable),
    macArm: find(/macOS.*arm64\.dmg$/i, RELEASE_FALLBACKS.macArm),
    macIntel: find(/macOS.*x86_64\.dmg$/i, RELEASE_FALLBACKS.macIntel),
  };
}

function MarketingPage({
  authenticated,
}: {
  authenticated: boolean;
}): ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [releaseLinks, setReleaseLinks] = useState<ReleaseLinks>({
    ...RELEASE_FALLBACKS,
  });

  useEffect(() => {
    const controller = new AbortController();
    void fetch("https://api.github.com/repos/10-X-eng/vibecad/releases", {
      headers: { Accept: "application/vnd.github+json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`GitHub returned ${response.status}`);
        return response.json() as Promise<unknown>;
      })
      .then((payload) => setReleaseLinks(resolveReleaseAssets(payload)))
      .catch((cause: unknown) => {
        if (!(cause instanceof DOMException && cause.name === "AbortError")) {
          setReleaseLinks({ ...RELEASE_FALLBACKS });
        }
      });
    return () => controller.abort();
  }, []);

  const closeMenu = (): void => setMenuOpen(false);
  return (
    <div className="marketing-site">
      <header className="marketing-navbar">
        <div className="marketing-nav-inner">
          <a className="marketing-logo" href="/" aria-label="VibeCAD home">
            <VibeCADLogo title="" />
            <span>VibeCAD</span>
          </a>
          <button
            className="mobile-toggle"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="marketing-navigation"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
          <nav
            id="marketing-navigation"
            className={
              menuOpen ? "marketing-nav-links open" : "marketing-nav-links"
            }
            aria-label="Main navigation"
          >
            <a href="/explore" onClick={closeMenu}>
              Explore
            </a>
            <a href="#features" onClick={closeMenu}>
              Features
            </a>
            <a href="#download" onClick={closeMenu}>
              Download
            </a>
            <a href="#get-started" onClick={closeMenu}>
              Get started
            </a>
            <a href={GITHUB_REPOSITORY} rel="noopener noreferrer">
              GitHub
            </a>
            <a className="nav-signin" href="/signin">
              Sign in
            </a>
            <a
              className="marketing-nav-cta"
              href={authenticated ? "/workspace" : "/signup"}
            >
              {authenticated ? "Open workspace" : "Create account"}
            </a>
          </nav>
        </div>
      </header>

      <main>
        <section className="marketing-hero">
          <div className="hero-glow" />
          <div className="grid-bg" />
          <div className="marketing-container hero-content">
            <div className="marketing-badge">
              ●&nbsp; AI-Native CAD Platform
            </div>
            <h1>
              Design Real Parts.
              <br />
              <span className="gradient-text">Just Describe Them.</span>
            </h1>
            <p className="marketing-subtitle">
              VibeCAD is an AI-native parametric CAD platform for designing real
              3D parts through conversation, focused modeling tools, and
              editable geometry history.
            </p>
            <div className="marketing-actions">
              <a className="marketing-button primary" href="#download">
                ↓ Download latest build
              </a>
              <a
                className="marketing-button secondary"
                href={GITHUB_REPOSITORY}
                rel="noopener noreferrer"
              >
                View on GitHub
              </a>
              <a className="marketing-button secondary" href="/explore">
                Explore public models
              </a>
              <a
                className="marketing-button secondary"
                href={authenticated ? "/workspace" : "/signup"}
              >
                {authenticated ? "Open workspace" : "Collaborate securely"}
              </a>
            </div>
            <div
              className="hero-visual"
              aria-label="Example AI modeling prompt"
            >
              <div className="hero-code">
                <p className="code-comment">// Tell VibeCAD what you need</p>
                <p>
                  <span className="code-keyword">prompt</span>:{" "}
                  <span className="code-string">
                    &quot;Create a mounting bracket
                  </span>
                </p>
                <p className="code-string">
                  &nbsp;&nbsp;with four M4 bolt holes,
                </p>
                <p className="code-string">&nbsp;&nbsp;3mm thick aluminum,</p>
                <p className="code-string">&nbsp;&nbsp;chamfered edges&quot;</p>
                <br />
                <p className="code-comment">
                  // VibeCAD generates parametric geometry
                </p>
                <p>
                  <span className="code-function">→ PartDesign</span>
                  .Pad(sketch, 3mm)
                </p>
                <p>
                  <span className="code-function">→ PartDesign</span>
                  .Pocket(holes, through)
                </p>
                <p>
                  <span className="code-function">→ PartDesign</span>
                  .Chamfer(edges, 0.5mm)
                </p>
                <br />
                <p className="code-comment">
                  // Every step is editable in the history tree
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="marketing-section features-section" id="features">
          <div className="marketing-container">
            <SectionHeading
              badge="Features"
              title={
                <>
                  CAD Meets <span className="gradient-text">Conversation</span>
                </>
              }
            >
              Professional parametric modeling powered by AI, with the precision
              engineers demand and the simplicity creators love.
            </SectionHeading>
            <div className="feature-grid">
              <FeatureCard symbol="◇" title="AI-Driven Modeling">
                Describe parts in natural language. VibeCAD translates your
                intent into precise parametric geometry using ChatGPT, Claude,
                Grok, or local models via Ollama.
              </FeatureCard>
              <FeatureCard symbol="⌁" title="Editable History Tree">
                Every operation lives in a parametric feature tree. Change a
                dimension and watch the entire model update, with full undo and
                redo.
              </FeatureCard>
              <FeatureCard symbol="⌘" title="Professional Workbenches">
                PartDesign, Sketcher, Assembly, FEM analysis, and more — a full
                CAD suite built on proven geometry kernels.
              </FeatureCard>
              <FeatureCard symbol="&lt;/&gt;" title="Script-First Workflows">
                Generate VibeScript, build123d, or OpenSCAD code. Every
                AI-generated part is reproducible code you can version, share,
                and modify.
              </FeatureCard>
              <FeatureCard symbol="⬡" title="Secure by Default">
                API keys stay in your OS keyring. No telemetry and no cloud
                lock-in. Share through passkeys, scoped access, and immutable
                history.
              </FeatureCard>
              <FeatureCard symbol="▣" title="Cross-Platform">
                Native builds for Linux, Windows, and macOS. Design locally and
                collaborate through the same secure workspace everywhere.
              </FeatureCard>
            </div>
          </div>
        </section>

        <section className="marketing-section" id="download">
          <div className="marketing-container">
            <SectionHeading
              badge="Download"
              title={
                <>
                  Get <span className="gradient-text">VibeCAD</span>
                </>
              }
            >
              Always the latest build, straight from the release pipeline. All
              downloads include SHA256 checksums.
            </SectionHeading>
            <div className="download-grid">
              <DownloadCard
                icon="◉"
                name="Linux"
                description="AppImage for any distro, or .deb for Debian and Ubuntu."
                links={[
                  ["AppImage (x86_64)", releaseLinks.appImage, true],
                  ["Debian package (.deb)", releaseLinks.deb, false],
                ]}
              />
              <DownloadCard
                icon="⊞"
                name="Windows"
                description="Installer for standard setup, or a portable 7z archive."
                links={[
                  ["Installer (.exe)", releaseLinks.windowsInstaller, true],
                  ["Portable (.7z)", releaseLinks.windowsPortable, false],
                ]}
              />
              <DownloadCard
                icon="●"
                name="macOS"
                description="Native DMG builds for Apple Silicon and Intel Macs."
                links={[
                  ["Apple Silicon (.dmg)", releaseLinks.macArm, true],
                  ["Intel (.dmg)", releaseLinks.macIntel, false],
                ]}
              />
            </div>
            <p className="release-info">
              Download links resolve against current release assets and safely
              fall back to GitHub&apos;s latest release URLs. View every build
              and checksum on{" "}
              <a href={RELEASES_URL} rel="noopener noreferrer">
                GitHub Releases
              </a>
              .
            </p>
          </div>
        </section>

        <section className="marketing-section getting-started" id="get-started">
          <div className="marketing-container">
            <SectionHeading
              badge="Get started"
              title={
                <>
                  Up and Running in{" "}
                  <span className="gradient-text">Minutes</span>
                </>
              }
            >
              From download to your first AI-generated part in three steps.
            </SectionHeading>
            <div className="steps-grid">
              <StepCard number="01" title="Install VibeCAD">
                Download the build for your platform. On Linux, make the
                AppImage executable or install the Debian package.
                <code>sudo apt install ./vibecad_*.deb</code>
              </StepCard>
              <StepCard number="02" title="Connect an AI Provider">
                Open Preferences → VibeCAD. Sign in with ChatGPT or connect
                OpenAI, Anthropic, xAI, Ollama, or another compatible endpoint.
              </StepCard>
              <StepCard number="03" title="Start Designing">
                Open a document, choose a workbench, and describe your part.
                Iterate through conversation while keeping a fully editable
                history tree.
              </StepCard>
            </div>
          </div>
        </section>

        <section className="marketing-section providers-section">
          <div className="marketing-container">
            <SectionHeading
              badge="Flexible AI"
              title={
                <>
                  Your Models,{" "}
                  <span className="gradient-text">Your Choice</span>
                </>
              }
            >
              Connect any provider. Use cloud APIs or run entirely locally with
              Ollama.
            </SectionHeading>
            <div className="provider-list">
              {[
                "ChatGPT Subscription",
                "OpenAI API",
                "Anthropic Claude",
                "xAI Grok",
                "Ollama (Local)",
                "OpenAI-Compatible",
              ].map((provider) => (
                <span className="provider-chip" key={provider}>
                  ● {provider}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="marketing-section marketing-cta">
          <div className="marketing-container">
            <h2>
              Ready to <span className="gradient-text">Vibe Your Parts?</span>
            </h2>
            <p>
              Download VibeCAD and start designing with AI today. Open source,
              cross-platform, no lock-in.
            </p>
            <div className="marketing-actions">
              <a className="marketing-button primary" href="#download">
                ↓ Download VibeCAD
              </a>
              <a
                className="marketing-button secondary"
                href={authenticated ? "/workspace" : "/signup"}
              >
                {authenticated
                  ? "Open workspace"
                  : "Create collaboration account"}
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="marketing-footer">
        <div className="marketing-container footer-inner">
          <a className="marketing-logo" href="/" aria-label="VibeCAD home">
            <VibeCADLogo title="" size={28} /> VibeCAD
          </a>
          <nav className="footer-links" aria-label="Project links">
            <a href="/explore">Explore</a>
            <a href={GITHUB_REPOSITORY} rel="noopener noreferrer">
              GitHub
            </a>
            <a href={RELEASES_URL} rel="noopener noreferrer">
              Releases
            </a>
            <a
              href={`${GITHUB_REPOSITORY}/blob/main/CONTRIBUTING.md`}
              rel="noopener noreferrer"
            >
              Contributing
            </a>
            <a
              href={`${GITHUB_REPOSITORY}/blob/main/LICENSE`}
              rel="noopener noreferrer"
            >
              License
            </a>
            <a
              href={`${GITHUB_REPOSITORY}/blob/main/SECURITY.md`}
              rel="noopener noreferrer"
            >
              Security
            </a>
          </nav>
          <p>© 2026 VibeCAD Contributors. Open source under LGPL.</p>
        </div>
      </footer>
    </div>
  );
}

function SectionHeading({
  badge,
  title,
  children,
}: {
  badge: string;
  title: ReactElement;
  children: string;
}): ReactElement {
  return (
    <div className="marketing-section-heading">
      <span className="marketing-badge">{badge}</span>
      <h2>{title}</h2>
      <p>{children}</p>
    </div>
  );
}

function FeatureCard({
  symbol,
  title,
  children,
}: {
  symbol: string;
  title: string;
  children: string;
}): ReactElement {
  return (
    <article className="feature-card">
      <span className="feature-icon" aria-hidden="true">
        {symbol}
      </span>
      <h3>{title}</h3>
      <p>{children}</p>
    </article>
  );
}

function DownloadCard({
  icon,
  name,
  description,
  links,
}: {
  icon: string;
  name: string;
  description: string;
  links: Array<[string, string, boolean]>;
}): ReactElement {
  return (
    <article className="download-card">
      <span className="os-icon" aria-hidden="true">
        {icon}
      </span>
      <h3>{name}</h3>
      <p>{description}</p>
      <div className="download-links">
        {links.map(([label, href, primary]) => (
          <a
            className={primary ? "download-button primary" : "download-button"}
            href={href}
            key={label}
          >
            {label}
          </a>
        ))}
      </div>
    </article>
  );
}

function StepCard({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: ReactNode;
}): ReactElement {
  return (
    <article className="step-card">
      <span>{number}</span>
      <h3>{title}</h3>
      <div>{children}</div>
    </article>
  );
}

function PublicNotFound(): ReactElement {
  return (
    <main className="public-not-found">
      <VibeCADLogo title="VibeCAD" size={52} />
      <p className="eyebrow">404 · Not found</p>
      <h1>This page doesn&apos;t exist.</h1>
      <a className="marketing-button primary" href="/">
        Return home
      </a>
    </main>
  );
}

function RecoveryCodesNotice({
  codes,
  onSaved,
}: {
  codes: string[];
  onSaved: () => void;
}): ReactElement {
  return (
    <div className="dialog-backdrop">
      <section
        className="dialog recovery-code-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recovery-codes-title"
      >
        <div className="dialog-warning">!</div>
        <h2 id="recovery-codes-title">Save these recovery codes now</h2>
        <p>
          This is the only time they will be shown. Store them offline. Each
          code can replace a lost passkey once.
        </p>
        <ol className="recovery-code-grid">
          {codes.map((code) => (
            <li key={code}>
              <code>{code}</code>
            </li>
          ))}
        </ol>
        <button
          className="button button-primary button-wide"
          onClick={onSaved}
          type="button"
        >
          I saved these codes
        </button>
      </section>
    </div>
  );
}

function AccountSecurity(): ReactElement {
  const { signOutAll } = useAuth();
  const [passkeys, setPasskeys] = useState<PasskeyRecord[]>([]);
  const [recoveryCount, setRecoveryCount] = useState(0);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [passkeyName, setPasskeyName] = useState("Backup passkey");
  const [pairingCode, setPairingCode] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  async function reload(): Promise<void> {
    const [passkeyPage, recovery] = await Promise.all([
      api.passkeys(),
      api.recoveryCodeStatus(),
    ]);
    setPasskeys(passkeyPage.items);
    setRecoveryCount(recovery.active);
  }

  useEffect(() => {
    void reload()
      .catch(() => setFailure("Security settings could not be loaded."))
      .finally(() => setBusy(false));
  }, []);

  async function addPasskey(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      const options = await api.addPasskeyOptions();
      const credential = await createPasskey(
        options.publicKey as JSONCreationOptions,
      );
      await api.addPasskeyVerify(
        options.challenge_id,
        passkeyName.trim() || "Passkey",
        credential,
      );
      await reload();
      setNotice("Passkey added.");
    } catch (cause) {
      setFailure(passkeyErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function revokePasskey(passkeyId: string): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      await api.revokePasskey(passkeyId);
      await reload();
      setNotice("Passkey revoked.");
    } catch {
      setFailure(
        "That passkey could not be revoked. Keep another recovery method first.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function replaceCodes(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      const result = await api.replaceRecoveryCodes();
      setNewCodes(result.recovery_codes);
      setRecoveryCount(result.recovery_codes.length);
      setNotice("Old recovery codes were revoked.");
    } catch {
      setFailure("Recovery codes could not be replaced.");
    } finally {
      setBusy(false);
    }
  }

  async function revokeCodes(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      await api.revokeRecoveryCodes();
      setRecoveryCount(0);
      setNewCodes(null);
      setNotice("Recovery codes revoked.");
    } catch {
      setFailure(
        "Keep at least one active passkey before revoking recovery codes.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function decidePairing(approve: boolean): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      await api.decidePairing(pairingCode, approve);
      setNotice(
        approve ? "Desktop pairing approved." : "Desktop pairing denied.",
      );
      setPairingCode("");
    } catch {
      setFailure("The pairing code is invalid, expired, or already decided.");
    } finally {
      setBusy(false);
    }
  }

  const finalPasskeyWithoutCodes = passkeys.length <= 1 && recoveryCount === 0;
  return (
    <main className="page security-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Account security</p>
          <h1>Passkeys and recovery</h1>
          <p>
            Manage phishing-resistant sign-in and approve VibeCAD desktop
            access.
          </p>
        </div>
      </div>
      {notice && <div className="alert alert-success">{notice}</div>}
      {failure && (
        <div className="alert alert-danger" role="alert">
          {failure}
        </div>
      )}
      <section className="security-card" aria-labelledby="passkeys-heading">
        <h2 id="passkeys-heading">Passkeys</h2>
        <div className="security-list">
          {passkeys.map((passkey) => (
            <div key={passkey.id}>
              <span>
                <strong>{passkey.name}</strong>
                <small>
                  Added {new Date(passkey.created_at).toLocaleDateString()}
                </small>
              </span>
              <button
                className="button button-danger"
                disabled={busy || finalPasskeyWithoutCodes}
                onClick={() => void revokePasskey(passkey.id)}
                type="button"
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
        <label htmlFor="passkey-name">New passkey name</label>
        <div className="inline-controls">
          <input
            id="passkey-name"
            maxLength={80}
            value={passkeyName}
            onChange={(event) => setPasskeyName(event.target.value)}
          />
          <button
            className="button button-secondary"
            disabled={busy}
            onClick={() => void addPasskey()}
            type="button"
          >
            Add backup passkey
          </button>
        </div>
      </section>
      <section
        className="security-card"
        aria-labelledby="recovery-heading-account"
      >
        <h2 id="recovery-heading-account">Recovery codes</h2>
        <p>{recoveryCount} unused recovery codes remain.</p>
        {newCodes && (
          <div className="one-time-codes" role="alert">
            <strong>Save now — these codes will not be shown again.</strong>
            <ol className="recovery-code-grid">
              {newCodes.map((code) => (
                <li key={code}>
                  <code>{code}</code>
                </li>
              ))}
            </ol>
            <button
              className="button button-primary"
              onClick={() => setNewCodes(null)}
              type="button"
            >
              I saved these codes
            </button>
          </div>
        )}
        <div className="inline-controls">
          <button
            className="button button-secondary"
            disabled={busy}
            onClick={() => void replaceCodes()}
            type="button"
          >
            Generate new codes
          </button>
          <button
            className="button button-danger"
            disabled={busy || recoveryCount === 0 || passkeys.length === 0}
            onClick={() => void revokeCodes()}
            type="button"
          >
            Revoke recovery codes
          </button>
        </div>
      </section>
      <section className="security-card" aria-labelledby="pairing-heading">
        <h2 id="pairing-heading">Pair VibeCAD desktop</h2>
        <p>
          Enter the short-lived code shown by VibeCAD. Approve only a device in
          front of you.
        </p>
        <label htmlFor="pairing-code">Pairing code</label>
        <input
          id="pairing-code"
          autoComplete="off"
          maxLength={11}
          placeholder="ABCDE-23456"
          value={pairingCode}
          onChange={(event) => setPairingCode(event.target.value.toUpperCase())}
        />
        <div className="inline-controls">
          <button
            className="button button-primary"
            disabled={busy || pairingCode.length < 10}
            onClick={() => void decidePairing(true)}
            type="button"
          >
            Approve desktop
          </button>
          <button
            className="button button-danger"
            disabled={busy || pairingCode.length < 10}
            onClick={() => void decidePairing(false)}
            type="button"
          >
            Deny
          </button>
        </div>
      </section>
      <section
        className="security-card danger-zone"
        aria-labelledby="sessions-heading"
      >
        <h2 id="sessions-heading">Sessions</h2>
        <p>Lost a device? Revoke every browser session, including this one.</p>
        <button
          className="button button-danger"
          disabled={busy}
          onClick={() => void signOutAll()}
          type="button"
        >
          Sign out all sessions
        </button>
      </section>
    </main>
  );
}

function NotFound(): ReactElement {
  return (
    <main className="page">
      <section className="empty-state error-state">
        <p className="eyebrow">404 · Not found</p>
        <h1>This page isn’t in the workspace.</h1>
        <p>Use the document library to return to a resource you can access.</p>
        <a className="button button-secondary" href="/workspace">
          Open documents
        </a>
      </section>
    </main>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
}
